import { useState, useMemo, useEffect } from 'react';
import { Plus, Search, TrendingUp, TrendingDown, Building2, Users as UsersIcon, FileText, Plug, MoreVertical, Pencil, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useCompaniesList } from '@/hooks/useCompaniesData';
import { toast } from 'sonner';

interface Company {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  logo?: string;
  description?: string;
  integrationStatus: 'connected' | 'partial' | 'none';
  userCount: number;
  createdAt: string;
  linkedInstanceId?: string;
  linkedInstanceName?: string;
  linkedProduct?: string;
}

interface Instance {
  instanceId: string;
  name: string;
  organisationId: string;
  organisationName: string;
}

export function CompanySettingsPage() {
  const navigate = useNavigate();
  const { apiGet, apiPost } = useApi();
  
  // Fetch companies using mode-aware query (demo mode uses mock data, live mode uses API)
  const { data: fetchedCompanies } = useCompaniesList();
  
  // Local state for companies (initialized from fetched data, can be modified locally)
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortOption, setSortOption] = useState<'name-asc' | 'name-desc' | 'newest' | 'oldest'>('name-asc');

  // Update local companies state when fetched data changes
  useEffect(() => {
    if (fetchedCompanies) {
      // Map CompanySettingsUI to local Company type
      const mappedCompanies: Company[] = fetchedCompanies.map(c => ({
        id: c.id,
        name: c.name,
        status: c.status,
        logo: c.logo,
        description: c.description,
        integrationStatus: c.integrationStatus,
        userCount: c.userCount,
        createdAt: c.createdAt,
        linkedInstanceId: c.linkedInstanceId,
        linkedInstanceName: c.linkedInstanceName,
        linkedProduct: c.linkedProduct,
      }));
      setCompanies(mappedCompanies);
    }
  }, [fetchedCompanies]);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isIntegrationDialogOpen, setIsIntegrationDialogOpen] = useState(false);
  const [isManageIntegrationsDialogOpen, setIsManageIntegrationsDialogOpen] = useState(false);
  
  // Form states
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    status: 'active' as 'active' | 'inactive',
    logo: '',
    description: '',
  });
  const [newCompanyName, setNewCompanyName] = useState('');

  // Instances state
  const [instances, setInstances] = useState<Instance[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');
    const [isLoadingInstances, setIsLoadingInstances] = useState(false);
    const [instancesError, setInstancesError] = useState<string | null>(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalCompanies = companies.length;
    const activeIntegrations = companies.filter(c => c.integrationStatus === 'connected').length;
    const activeUsers = companies.reduce((sum, c) => sum + c.userCount, 0);
    const documentTemplates = 15; // Mock value

    return {
      totalCompanies: { value: totalCompanies, trend: 12 },
      activeIntegrations: { value: activeIntegrations, trend: 8 },
      activeUsers: { value: activeUsers, trend: -3 },
      documentTemplates: { value: documentTemplates, trend: 5 },
    };
  }, [companies]);

  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(company => company.status === statusFilter);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        default:
          return 0;
      }
    });

    return sorted;
  }, [companies, searchTerm, statusFilter, sortOption]);

  const handleAddCompany = () => {
    const newCompany: Company = {
      id: Date.now().toString(),
      name: formData.name,
      status: formData.status,
      logo: formData.logo || undefined,
      description: formData.description,
      integrationStatus: 'none',
      userCount: 0,
      createdAt: new Date().toISOString(),
    };

    setCompanies([...companies, newCompany]);
    setNewCompanyName(formData.name);
    setIsAddDialogOpen(false);
    setFormData({ name: '', status: 'active', logo: '', description: '' });
    setIsIntegrationDialogOpen(true);
  };

  const handleEditCompany = () => {
    if (!selectedCompany) return;

    setCompanies(companies.map(c =>
      c.id === selectedCompany.id
        ? { ...c, ...formData }
        : c
    ));

    setIsEditDialogOpen(false);
    setSelectedCompany(null);
    setFormData({ name: '', status: 'active', logo: '', description: '' });
  };

  const handleDeleteCompany = () => {
    if (!selectedCompany) return;

    setCompanies(companies.filter(c => c.id !== selectedCompany.id));
    setIsDeleteDialogOpen(false);
    setSelectedCompany(null);
  };

  const openEditDialog = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      status: company.status,
      logo: company.logo || '',
      description: company.description || '',
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (company: Company) => {
    setSelectedCompany(company);
    setIsDeleteDialogOpen(true);
  };

  const handleViewCompany = (company: Company) => {
    // Navigate to company detail view or show details
    console.log('Viewing company:', company);
    // For now, open edit dialog to view/edit details
    openEditDialog(company);
  };

    const handleManageIntegrations = (company: Company) => {
      setSelectedCompany(company);
      setSelectedProduct(company.linkedProductName || '');
      setSelectedInstanceId(company.linkedInstanceId || '');
      setInstances([]);
      setIsManageIntegrationsDialogOpen(true);
    };

  // Fetch instances when product is selected
  useEffect(() => {
    if (selectedProduct === 'Financials') {
      setIsLoadingInstances(true);
      setInstancesError(null);
      apiGet<Instance[]>('/api/v1/externalInstances')
        .then((data) => {
          setInstances(data);
          setIsLoadingInstances(false);
        })
        .catch((error) => {
          console.error('Error fetching instances:', error);
          setInstancesError('Failed to load instances. Please try again.');
          setIsLoadingInstances(false);
        });
    } else {
      setInstances([]);
      setSelectedInstanceId('');
    }
  }, [selectedProduct, apiGet]);

    const handleSaveIntegration = async () => {
      if (!selectedCompany) return;

      const selectedInstance = instances.find(i => i.instanceId === selectedInstanceId);
      const linkedInstanceName = selectedInstance?.name || undefined;

            try {
              // Call the API to persist the integration linking
              // Note: LinkedProductId is set by the backend using WebhookConstants.FinancialsProductId
              await apiPost(`/api/v1/company/${selectedCompany.id}`, {
                linkedSubscriptionId: selectedInstanceId || null,
                linkedSubscriptionName: linkedInstanceName || null,
                linkedProductName: selectedProduct || null,
              });

        // Update local state on success
        setCompanies(companies.map(c =>
          c.id === selectedCompany.id
            ? { 
                ...c, 
                linkedInstanceId: selectedInstanceId || undefined,
                linkedInstanceName: linkedInstanceName,
                linkedProductName: selectedProduct || undefined,
                integrationStatus: selectedInstanceId ? 'connected' : 'none'
              }
            : c
        ));

        setIsManageIntegrationsDialogOpen(false);
        setSelectedCompany(null);
        setSelectedProduct('');
        setSelectedInstanceId('');
      } catch (error) {
        console.error('Error saving company integration:', error);
        toast.error('Failed to save company integration');
        setIsManageIntegrationsDialogOpen(false);
        setSelectedCompany(null);
        setSelectedProduct('');
        setSelectedInstanceId('');
      }
    };

    const handleCancelIntegration = () => {
      setIsManageIntegrationsDialogOpen(false);
      setSelectedCompany(null);
      setSelectedProduct('');
      setSelectedInstanceId('');
    };

        const handleRemoveLinkClick = () => {
          setShowRemoveConfirm(true);
        };

                const handleConfirmRemoveLinkIntegration = async () => {
                  if (!selectedCompany) return;

                  try {
                    // Note: LinkedProductId is set by the backend based on LinkedSubscriptionId
                    await apiPost(`/api/v1/company/${selectedCompany.id}`, {
                      linkedSubscriptionId: null,
                      linkedSubscriptionName: null,
                      linkedProductName: null,
                    });

                    setCompanies(companies.map(c =>
                      c.id === selectedCompany.id
                        ? { 
                            ...c, 
                            linkedInstanceId: undefined,
                            linkedInstanceName: undefined,
                            linkedProductName: undefined,
                            integrationStatus: 'none'
                          }
                        : c
                    ));

            setShowRemoveConfirm(false);
            setIsManageIntegrationsDialogOpen(false);
            setSelectedCompany(null);
            setSelectedProduct('');
            setSelectedInstanceId('');
            toast.success('Company integration removed successfully');
          } catch (error) {
            console.error('Error removing company integration:', error);
            toast.error('Failed to remove company integration');
            setShowRemoveConfirm(false);
            setIsManageIntegrationsDialogOpen(false);
            setSelectedCompany(null);
            setSelectedProduct('');
            setSelectedInstanceId('');
          }
        };

    const getIntegrationStatusBadge= (status: Company['integrationStatus'], company: Company) => {
    const handleBadgeClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleManageIntegrations(company);
    };

    switch (status) {
      case 'connected':
        return (
          <Badge 
            className="bg-green-500/10 text-green-500 hover:bg-green-500/20 cursor-pointer transition-colors"
            onClick={handleBadgeClick}
          >
            <Plug className="h-3 w-3 mr-1" />Connected
          </Badge>
        );
      case 'partial':
        return (
          <Badge 
            className="bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20 cursor-pointer transition-colors"
            onClick={handleBadgeClick}
          >
            <Plug className="h-3 w-3 mr-1" />Partial
          </Badge>
        );
      case 'none':
        return (
          <Badge 
            variant="outline" 
            className="cursor-pointer hover:bg-accent transition-colors"
            onClick={handleBadgeClick}
          >
            <Plug className="h-3 w-3 mr-1" />Not Connected
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your companies, integrations, and organizational settings
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-shadow hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Companies</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCompanies.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {stats.totalCompanies.trend > 0 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-green-500">+{stats.totalCompanies.trend}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                  <span className="text-red-500">{stats.totalCompanies.trend}%</span>
                </>
              )}
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer transition-shadow hover:shadow-md"
          onClick={() => navigate('/settings/integrations')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Integrations</CardTitle>
            <Plug className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeIntegrations.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {stats.activeIntegrations.trend > 0 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-green-500">+{stats.activeIntegrations.trend}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                  <span className="text-red-500">{stats.activeIntegrations.trend}%</span>
                </>
              )}
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-shadow hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <UsersIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeUsers.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {stats.activeUsers.trend > 0 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-green-500">+{stats.activeUsers.trend}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                  <span className="text-red-500">{stats.activeUsers.trend}%</span>
                </>
              )}
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/settings/templates')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Document Templates</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.documentTemplates.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              {stats.documentTemplates.trend > 0 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                  <span className="text-green-500">+{stats.documentTemplates.trend}%</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                  <span className="text-red-500">{stats.documentTemplates.trend}%</span>
                </>
              )}
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Companies</CardTitle>
          <CardDescription>Manage your company profiles and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOption} onValueChange={(value: any) => setSortOption(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Companies Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Integration</TableHead>
                  <TableHead>Linked Database</TableHead>
                  <TableHead>Linked Product</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No companies found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedCompanies.map((company) => (
                    <TableRow 
                      key={company.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleViewCompany(company)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={company.logo} />
                            <AvatarFallback>{company.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{company.name}</div>
                            {company.description && (
                              <div className="text-sm text-muted-foreground">{company.description}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={company.status === 'active' ? 'default' : 'secondary'}>
                          {company.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getIntegrationStatusBadge(company.integrationStatus, company)}</TableCell>
                      <TableCell>{company.linkedInstanceName || '-'}</TableCell>
                      <TableCell>{company.linkedProduct || '-'}</TableCell>
                      <TableCell>{company.userCount}</TableCell>
                      <TableCell>{formatDate(company.createdAt)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewCompany(company)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(company)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManageIntegrations(company)}>
                              <Plug className="h-4 w-4 mr-2" />
                              Manage Integrations
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(company)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add Company Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
            <DialogDescription>
              Create a new company profile. You can set up integrations after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter company description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="status">Active Status</Label>
              <Switch
                id="status"
                checked={formData.status === 'active'}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, status: checked ? 'active' : 'inactive' })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo">Logo URL</Label>
              <Input
                id="logo"
                placeholder="Enter logo URL (optional)"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCompany} disabled={!formData.name}>
              Save Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company information and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Company Name *</Label>
              <Input
                id="edit-name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter company description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-status">Active Status</Label>
              <Switch
                id="edit-status"
                checked={formData.status === 'active'}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, status: checked ? 'active' : 'inactive' })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-logo">Logo URL</Label>
              <Input
                id="edit-logo"
                placeholder="Enter logo URL (optional)"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCompany} disabled={!formData.name}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selectedCompany?.name}</strong> and all associated data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCompany} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Integration Setup Dialog */}
      <Dialog open={isIntegrationDialogOpen} onOpenChange={setIsIntegrationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Up Integrations</DialogTitle>
            <DialogDescription>
              Would you like to set up integrations for <strong>{newCompanyName}</strong> now?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIntegrationDialogOpen(false)}>
              Skip for Now
            </Button>
            <Button onClick={() => {
              setIsIntegrationDialogOpen(false);
              navigate('/settings/integrations');
            }}>
              Yes, Set Up Integrations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Integrations Dialog */}
      <Dialog open={isManageIntegrationsDialogOpen} onOpenChange={setIsManageIntegrationsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Integrations</DialogTitle>
            <DialogDescription>
              Link <strong>{selectedCompany?.name}</strong> to an instance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product">Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Financials">Financials</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedProduct && (
              <div className="space-y-2">
                <Label htmlFor="instance">Instance</Label>
                {isLoadingInstances ? (
                  <div className="text-sm text-muted-foreground">Loading instances...</div>
                ) : instancesError ? (
                  <div className="text-sm text-destructive">{instancesError}</div>
                ) : (
                  <Select value={selectedInstanceId} onValueChange={setSelectedInstanceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an instance" />
                    </SelectTrigger>
                    <SelectContent>
                      {instances.map((instance) => (
                        <SelectItem key={instance.instanceId} value={instance.instanceId}>
                          {instance.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              {selectedCompany?.linkedInstanceId && (
                                <Button variant="outline" className="text-destructive hover:text-destructive" onClick={handleRemoveLinkClick}>
                                  Remove Link
                                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelIntegration}>
                Cancel
              </Button>
              <Button onClick={handleSaveIntegration} disabled={isLoadingInstances}>
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Integration Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the integration link for <strong>{selectedCompany?.name}</strong>? This will unlink the company from the external instance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemoveLinkIntegration} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
